import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Lock, User, Phone, Users, Lock as LockClosed } from 'lucide-react';
import { useEventConfig } from '../hooks/useEventConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { user, token, login } = useContext(AuthContext);
  const { config } = useEventConfig();
  const [step, setStep] = useState(user ? 2 : 1);
  const [loading, setLoading] = useState(false);

  const [isLogin, setIsLogin] = useState(true);
  const [authData, setAuthData] = useState({ email: '', password: '' });

  const [registrationData, setRegistrationData] = useState({
    full_name: '',
    phone: '',
    level: '',
    partner_name: '',
  });

  const levels = config.categories;
  const priceLabel = `R$ ${config.price.toFixed(2).replace('.', ',')}`;

  if (!config.registrations_open) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-10 text-center"
        data-testid="registrations-closed-banner"
      >
        <LockClosed className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="font-unbounded font-bold text-2xl mb-3">Inscrições Encerradas</h3>
        <p className="text-muted-foreground">
          As inscrições para <span className="text-primary font-bold">{config.event_name}</span> não estão disponíveis no momento.
        </p>
        <p className="text-muted-foreground text-sm mt-2">
          Acompanhe nossos canais para saber quando o próximo desafio abrir.
        </p>
      </motion.div>
    );
  }

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, authData);
        login(response.data.access_token, response.data.user);
        toast.success('Login realizado!');
        setStep(2);
      } else {
        const response = await axios.post(`${API}/auth/register`, {
          email: authData.email,
          password: authData.password,
          full_name: 'Temp',
          phone: '00000000000'
        });
        login(response.data.access_token, response.data.user);
        toast.success('Conta criada! Complete seus dados.');
        setStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar requisição');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    if (!registrationData.level) {
      toast.error('Selecione sua categoria');
      return;
    }
    if (!registrationData.partner_name.trim()) {
      toast.error('Informe o nome da sua dupla');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, registrationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inscrição realizada! Prossiga para o pagamento.');
      navigate('/pagamento');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar inscrição');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {step === 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8"
        >
          <div className="mb-8">
            <h3 className="font-unbounded font-bold text-2xl mb-2">
              {isLogin ? 'Fazer Login' : 'Criar Conta'}
            </h3>
            <p className="text-muted-foreground">
              {isLogin ? 'Entre com sua conta' : 'Cadastre-se para continuar'}
            </p>
          </div>

          <form onSubmit={handleAuth} className="space-y-6">
            <div>
              <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="email" data-testid="email-input" type="email" required
                  value={authData.email}
                  onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
                  className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                  placeholder="seu@email.com" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="password" data-testid="password-input" type="password" required
                  value={authData.password}
                  onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                  className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                  placeholder="••••••••" />
              </div>
            </div>
            <Button data-testid="auth-submit-button" type="submit" disabled={loading}
              className="w-full bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 h-12 skew-btn">
              <span>{loading ? 'Processando...' : (isLogin ? 'Entrar' : 'Criar Conta')}</span>
            </Button>
            <div className="text-center">
              <button type="button" onClick={() => setIsLogin(!isLogin)}
                className="text-muted-foreground hover:text-primary transition-colors text-sm">
                {isLogin ? 'Não tem conta? Cadastre-se' : 'Já tem conta? Faça login'}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8"
        >
          <div className="mb-8">
            <h3 className="font-unbounded font-bold text-2xl mb-2">Detalhes da Inscrição</h3>
            <p className="text-muted-foreground">Complete suas informações</p>
          </div>

          <form onSubmit={handleRegistration} className="space-y-8">
            <div>
              <Label htmlFor="full_name" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Nome Completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="full_name" data-testid="fullname-input" type="text" required
                  value={registrationData.full_name}
                  onChange={(e) => setRegistrationData({ ...registrationData, full_name: e.target.value })}
                  className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                  placeholder="Seu nome completo" />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Telefone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="phone" data-testid="phone-input" type="tel" required
                  value={registrationData.phone}
                  onChange={(e) => setRegistrationData({ ...registrationData, phone: e.target.value })}
                  className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                  placeholder="(00) 00000-0000" />
              </div>
            </div>

            <div>
              <Label htmlFor="partner_name" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Nome da sua dupla</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input id="partner_name" data-testid="partner-name-input" type="text" required
                  value={registrationData.partner_name}
                  onChange={(e) => setRegistrationData({ ...registrationData, partner_name: e.target.value })}
                  className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                  placeholder="Nome completo do parceiro(a)" />
              </div>
              <p className="text-muted-foreground text-xs mt-2">
                Cada um deve fazer sua própria inscrição informando o nome do parceiro(a).
              </p>
            </div>

            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4 block">Escolha sua categoria</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {levels.map((level) => (
                  <button
                    key={level}
                    data-testid={`level-${level.toLowerCase()}-button`}
                    type="button"
                    onClick={() => setRegistrationData({ ...registrationData, level })}
                    className={`p-6 border transition-all duration-200 ${
                      registrationData.level === level
                        ? 'border-primary bg-primary/10'
                        : 'border-white/10 hover:border-primary/50'
                    }`}
                  >
                    <p className="font-unbounded font-bold text-xl">{level}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/30 p-6 text-center">
              <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Valor</p>
              <p className="text-4xl font-bold text-primary" data-testid="registration-price">{priceLabel}</p>
            </div>

            <Button
              data-testid="registration-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 h-12 skew-btn"
            >
              <span>{loading ? 'Processando...' : 'Prosseguir para Pagamento'}</span>
            </Button>
          </form>
        </motion.div>
      )}
    </div>
  );
};

export default RegistrationForm;
